import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  hasExceededDocumentationThreshold,
  hasExceededMaxValueForAuthorizedAdvisor,
  isWithinValidOtpRange,
} from "./utils";

import { StyledRow, WrapLgMarginBottom } from "App/global-style";
import { AdviceProductDetailsFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { OneTimePaymentAdvisorChoice } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/interfaces";
import { UwInlineAlert } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/styled";
import { Description } from "Components/Features/CheckPrice/Products/Advice/adviceStyled";
import { NumericInput } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { RadioButton } from "Components/Form/RadioButton";
import { VALUE_TYPE } from "Components/NumberValue";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { UserAuthorizationLevel } from "Store/advice/interfaces";
import { isAllowed } from "Store/akaAdvice/interfaces/getters";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { TextLanguage } from "Store/texts/interfaces";
import { useDebounce } from "Utils/debounce";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import { FormikProps } from "formik";
import React from "react";
import { InsurancePrice } from "../../../AdvicePrice/InsurancePrice";
import { CloseToMaxAgeWarning } from "../../../CloseToMaxAgeWarning";
import { AkaAdvicePriceFormValues } from "../../../interfaces";
import {
  DISABILITY_CAPITAL_END_AGE,
  DISABILITY_CAPITAL_MAX_AGE,
} from "../../disabilityAdviceCardConstants";
import { getDisabilityOneTimePaymentAdvisorChoiceOptions } from "../utils";
import { PriceProps } from "./disabilityOneTimePaymentTypes";

type Props = PriceProps & {
  adviceDetails: AdviceProductDetailsFormValues;
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  recalculatePrice: (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ) => void;
  adviceCard: any;
  lang: TextLanguage;
};

export const DisabilityOneTimePaymentAdvisorForm = ({
  adviceDetails,
  formProps,
  need,
  recalculatePrice,
  adviceCard,
  lang,
  ...priceProps
}: Props): React.ReactElement | null => {
  const coverageName = ProductCoverageName.DISABILITY_FLEX_CAPITAL;
  const debounce = useDebounce(1000);
  const twoTimesIncome = adviceDetails.income * 2;
  const oneTimePaymentValue = getCorrectAkaAdviceValues(
    need,
    coverageName,
    true,
    UserAuthorizationLevel.PRODUCT_MANAGER,
  ).maxAdviceValue;

  const handleCustomOneTimePaymentAdvisor = (
    value: number | undefined,
  ): void => {
    if (
      isDefined(value) &&
      isWithinValidOtpRange(twoTimesIncome, value, need)
    ) {
      recalculatePrice(
        {
          ...formProps.values,
          disabilityOneTimePaymentAdvisorValue: value,
        },
        INSURANCE_NAME_VALUE.DISABILITY_FLEX,
      );
    } else {
      formProps.setFieldTouched("disabilityOneTimePaymentAdvisorValue", true);
    }
  };

  if (!isAllowed(need, coverageName)) {
    return null;
  }

  return (
    <WrapLgMarginBottom>
      <Description fw={700} size="16px">
        Engangsutbetaling
      </Description>
      <StyledRow className="DisabilityOneTimePaymentAdvisorForm">
        <div>
          {formProps.values.disabilityInsuredSumCheckbox && (
            <>
              <StyledRow className="OneTimePaymentRadio">
                <RadioButton
                  name="disabilityOneTimePaymentAdvisorChoice"
                  id="disabilityOneTimePaymentAdvisorChoice"
                  label={"Storebrand uføreforsikring - Engangsutbetaling"}
                  options={getDisabilityOneTimePaymentAdvisorChoiceOptions(
                    need,
                    adviceDetails,
                    oneTimePaymentValue,
                    adviceCard.disabilityOneTimePayment,
                    lang,
                  )}
                  direction="vertical"
                  error={getError(
                    "disabilityOneTimePaymentAdvisorChoice",
                    formProps,
                  )}
                  onChange={(e) =>
                    recalculatePrice(
                      {
                        ...formProps.values,
                        disabilityOneTimePaymentAdvisorChoice: e.target
                          .value as OneTimePaymentAdvisorChoice,
                      },
                      INSURANCE_NAME_VALUE.DISABILITY_FLEX,
                    )
                  }
                />
              </StyledRow>
              {formProps.values.disabilityOneTimePaymentAdvisorChoice ===
                OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT && (
                <StyledRow>
                  <NumericInput
                    large
                    id="disabilityOneTimePaymentAdvisorValue"
                    name="disabilityOneTimePaymentAdvisorValue"
                    label={
                      adviceCard.disabilityOneTimePayment.advisorTexts
                        .customOneTimePaymentAdvisorLabel[lang]
                    }
                    suffix={VALUE_TYPE.NOK}
                    error={getError(
                      "disabilityOneTimePaymentAdvisorValue",
                      formProps,
                    )}
                    onChange={(value) => {
                      debounce(() =>
                        handleCustomOneTimePaymentAdvisor(value.floatValue),
                      );
                    }}
                    disabled={
                      priceProps.displayPrice && priceProps.priceIsLoading
                    }
                    inputMode={InputMode.NUMERIC}
                    inputProps={{ autoComplete: "off" }}
                  />
                  {priceProps.displayPrice && priceProps.priceIsLoading && (
                    <ThemedSpinner black />
                  )}
                </StyledRow>
              )}
              {hasExceededDocumentationThreshold(
                oneTimePaymentValue,
                need,
                formProps.values,
              ) && (
                <StyledRow>
                  <UwInlineAlert
                    title={
                      adviceCard.disabilityOneTimePayment.advisorTexts
                        .uwMaxAdvisor[lang]
                    }
                  />
                </StyledRow>
              )}
              {hasExceededMaxValueForAuthorizedAdvisor(
                oneTimePaymentValue,
                need,
                formProps.values,
              ) && (
                <>
                  <StyledRow>
                    <UwInlineAlert
                      title={
                        adviceCard.disabilityOneTimePayment.advisorTexts
                          .uwMaxAuthAdvisor[lang]
                      }
                    />
                  </StyledRow>
                  <StyledRow>
                    <UwInlineAlert
                      type="warning"
                      title={
                        adviceCard.disabilityOneTimePayment.advisorTexts
                          .uwMaxAuthAdvisorWarning[lang]
                      }
                    />
                  </StyledRow>
                </>
              )}
            </>
          )}
        </div>
        {priceProps.displayPrice && (
          <InsurancePrice
            needName={NeedName.DISABILITY}
            productCoverages={[coverageName]}
            priceIsChanging={priceProps.priceIsChanging}
            priceIsLoading={priceProps.priceIsLoading}
          />
        )}
      </StyledRow>
      <CloseToMaxAgeWarning
        need={need}
        coverageName={coverageName}
        warningTexts={adviceCard.warnings}
        lang={lang}
        warningVariables={{
          maxAge: DISABILITY_CAPITAL_MAX_AGE,
          endAge: DISABILITY_CAPITAL_END_AGE,
        }}
      />
    </WrapLgMarginBottom>
  );
};
